/**
 * AB Quick Add (collection product grid)
 *
 * - Desktop (>= 768px): right-side drawer opened from the card's "Quick add" link.
 * - Mobile (< 768px): bottom sheet opened from the card's bottom bar.
 *   Both show every available colorway (PDP swatch groups) and its sizes, using
 *   data from /products/<handle>?view=ab-quick-add (templates/product.ab-quick-add.liquid).
 * - Desktop flip cards: keeps the "+N" overflow pill in sync with the size row.
 *
 * Add to cart goes through the theme's own cart composable (use-focus-management.js),
 * imported by relative path so it shares the same module instance — and therefore the
 * same cart state and cart drawer — as the section bundles.
 */

const VIEW = 'ab-quick-add';
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const desktopQuery = window.matchMedia('(min-width: 768px)');
const cache = new Map();
const MODES = ['link', 'flip', 'off'];

/* Desktop option switch: ?qa=link | ?qa=flip | ?qa=off (kept for the session). Default: link. */
(function initMode() {
  let mode = new URLSearchParams(window.location.search).get('qa');
  try {
    if (mode && MODES.includes(mode)) window.sessionStorage.setItem('ab-qa-mode', mode);
    else mode = window.sessionStorage.getItem('ab-qa-mode');
  } catch (error) {
    /* storage unavailable */
  }
  document.documentElement.dataset.abQa = MODES.includes(mode) ? mode : 'link';
})();

const handleFromCard = (card) => {
  const link = card && (card.querySelector('a[data-product-link]') || card.querySelector('a[data-product-images]'));
  const href = link && link.getAttribute('href');
  if (!href) return null;
  const match = new URL(href, window.location.origin).pathname.match(/\/products\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

let cartApi = null;
let state = null;
let lastTrigger = null;
let closeTimer = null;

/* ---------- utils ---------- */

const esc = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const sizeLabel = (title, unisex) => {
  if (!title) return '';
  const clean = unisex && title.includes('(') ? title.split(' (')[0].trim() : title;
  return clean.replace('UK ', '');
};

const editionLabel = (edition) => {
  if (!edition) return '';
  return `(${edition.charAt(0).toUpperCase()}${edition.slice(1)} Edition)`;
};

async function getCartApi() {
  if (cartApi) return cartApi;
  try {
    const mod = await import('./use-focus-management.js');
    const cart = typeof mod.u === 'function' ? mod.u() : null;
    const drawer = typeof mod.b === 'function' ? mod.b() : null;
    if (cart && typeof cart.addSingleVariant === 'function' && drawer && typeof drawer.openDrawer === 'function') {
      cartApi = { add: cart.addSingleVariant, openDrawer: drawer.openDrawer };
    }
  } catch (error) {
    console.warn('[AB quick add] theme cart module unavailable, using fallback', error);
  }
  return cartApi;
}

async function addToCart(variantId, properties) {
  const api = await getCartApi();
  if (api) {
    await api.add(variantId, 1, properties);
    api.openDrawer();
    return;
  }
  // Fallback: plain Ajax API, then go to the cart page.
  await fetch(`${ROOT}cart/add.js`, {
    body: JSON.stringify({ items: [{ id: variantId, properties, quantity: 1 }] }),
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  });
  window.location.href = `${ROOT}cart`;
}

async function getData(handle) {
  if (cache.has(handle)) return cache.get(handle);
  const request = fetch(`${ROOT}products/${encodeURIComponent(handle)}?view=${VIEW}`, { credentials: 'same-origin' })
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    })
    .catch((error) => {
      cache.delete(handle);
      throw error;
    });
  cache.set(handle, request);
  return request;
}

/* ---------- DOM ---------- */

const ICON_CLOSE =
  '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
const ICON_SOLD_OUT =
  '<svg class="pointer-events-none absolute inset-0 z-20 size-full" viewBox="0 0 10 10" preserveAspectRatio="none" aria-hidden="true"><line x1="10" y1="0" x2="0" y2="10" stroke="currentColor" stroke-width="1px" vector-effect="non-scaling-stroke"/></svg>';

let root = null;
let panel = null;

function ensureRoot() {
  if (root) return;
  root = document.createElement('div');
  root.className = 'ab-qa';
  root.hidden = true;
  root.innerHTML =
    '<div class="ab-qa__scrim" data-ab-qa-close></div>' +
    '<div class="ab-qa__panel" role="dialog" aria-modal="true" aria-label="Select a size" tabindex="-1"></div>';
  document.body.appendChild(root);
  panel = root.querySelector('.ab-qa__panel');
  root.addEventListener('click', onPanelClick);
  root.addEventListener('keydown', onPanelKeydown);
}

function currentItem() {
  return state.data.items[state.itemIndex];
}

function renderSwatch(item, index, allOnSale) {
  const selected = index === state.itemIndex;
  return (
    `<button type="button" class="relative size-9 snap-center rounded-full" style="cursor:pointer" ` +
    `data-ab-qa-color="${index}" aria-label="${esc(item.colorFull)} swatch" aria-pressed="${selected}" title="${esc(item.colorFull)}">` +
    `<span class="inset-ring-light-charcoal-0.7 pointer-events-none absolute inset-0 block size-full rounded-full border-3 border-white inset-ring-1${
      selected ? ' shadow-[0_0_0_1px_#212121]' : ''
    }" style="background: ${esc(item.swatch)}"></span>` +
    (item.onSale && !allOnSale
      ? '<span class="bg-light-charcoal z-background-overlay absolute right-0.5 bottom-[-4px] left-0.5 rounded-full text-center text-[9px] leading-4 font-medium tracking-wider text-white uppercase">sale</span>'
      : '') +
    '</button>'
  );
}

function renderColors() {
  const { items } = state.data;
  if (items.length < 2) return '';
  const item = currentItem();
  const allOnSale = items.every((i) => i.onSale);
  const groups = [];
  items.forEach((it, index) => {
    const key = it.group || '';
    let group = groups.find((g) => g.key === key);
    if (!group) {
      group = { key, entries: [] };
      groups.push(group);
    }
    group.entries.push([it, index]);
  });
  const showTitles = groups.filter((g) => g.key).length > 1;
  const groupsHtml = groups
    .map(
      (g) =>
        `<div class="ab-qa__group">${showTitles && g.key ? `<p class="ab-qa__group-title">${esc(g.key)}</p>` : ''}` +
        `<div class="swatch-group flex flex-wrap items-center gap-1">${g.entries
          .map(([it, index]) => renderSwatch(it, index, allOnSale))
          .join('')}</div></div>`
    )
    .join('');
  return (
    '<div class="ab-qa__colors">' +
    `<p class="ab-qa__color-row"><span class="ab-qa__mono">Color</span>` +
    `<span class="ab-qa__color-name">${esc(item.color)}</span>` +
    `<span class="ab-qa__edition">${esc(editionLabel(item.edition))}</span></p>` +
    groupsHtml +
    '</div>'
  );
}

function renderSizes() {
  const item = currentItem();
  const minWidth = state.data.unisex ? '96px' : state.mode === 'drawer' ? '52px' : '48px';
  const buttons = item.variants
    .map((v) => {
      const selected = state.variantId === v.id && v.available;
      return (
        `<li><button type="button" class="btn-size${selected ? ' btn-size--selected' : ''}" data-ab-qa-size="${v.id}" ` +
        `${v.available ? '' : 'disabled '}aria-label="Select size ${esc(sizeLabel(v.title, state.data.unisex))}${
          v.available ? '' : ', sold out'
        }">` +
        `<span class="pointer-events-none block">${esc(sizeLabel(v.title, state.data.unisex))}</span>` +
        (v.available ? '' : ICON_SOLD_OUT) +
        '</button></li>'
      );
    })
    .join('');
  return (
    `<div class="ab-qa__sizes"><p class="ab-qa__mono">${esc(state.data.sizeLabel)}</p>` +
    `<ul class="grid-size-selector" style="--size-btn-min-width:${minWidth}">${buttons}</ul></div>`
  );
}

function renderPrice(item) {
  return (
    `<span${item.onSale ? ' class="text-red"' : ''}>${esc(item.price)}</span>` +
    (item.onSale ? `<span class="line-through opacity-65">${esc(item.compare)}</span>` : '')
  );
}

function ctaLabel() {
  const item = currentItem();
  if (state.adding) return 'Adding…';
  if (!state.variantId) return 'Select a size';
  return `Add to cart - ${item.price}`;
}

function render() {
  const item = currentItem();
  const header =
    '<div class="ab-qa__header"><h2 class="ab-qa__title">Select a size</h2>' +
    `<button type="button" class="ab-qa__close" data-ab-qa-close aria-label="Close">${ICON_CLOSE}</button></div>`;

  let html = '';
  if (state.mode === 'drawer') {
    html =
      header +
      '<div class="ab-qa__body" data-ab-qa-body>' +
      `<div class="ab-qa__image">${item.image ? `<img src="${esc(item.image)}" alt="${esc(state.data.masterName)} - ${esc(item.colorFull)}" width="800" height="800">` : ''}</div>` +
      `<div class="ab-qa__product"><p class="ab-qa__name">${esc(state.data.masterName)}</p><p class="ab-qa__price">${renderPrice(item)}</p></div>` +
      renderColors() +
      renderSizes() +
      '</div>' +
      '<div class="ab-qa__footer">' +
      `<button type="button" class="btn btn-secondary w-full py-4 tracking-wider hover:border-black disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-200 disabled:text-gray-400" data-ab-qa-add ${
        state.variantId && !state.adding ? '' : 'disabled'
      }>${esc(ctaLabel())}</button>` +
      `<a class="ab-qa__details" href="${esc(item.url)}">View full details</a>` +
      '</div>';
  } else {
    html =
      '<div class="ab-qa__grabber" aria-hidden="true"></div>' +
      header +
      '<div class="ab-qa__body" data-ab-qa-body>' +
      '<div class="ab-qa__product-row"><div class="ab-qa__product-text">' +
      `<p class="ab-qa__product-name">${esc(state.data.masterName)}</p>` +
      `<p class="ab-qa__product-color">${esc(item.colorFull)}</p>` +
      `<p class="ab-qa__product-price">${renderPrice(item)}</p></div>` +
      (item.image ? `<img class="ab-qa__thumb" src="${esc(item.image)}" alt="" width="128" height="128">` : '') +
      '</div>' +
      renderColors() +
      renderSizes() +
      '</div>';
  }

  const body = panel.querySelector('[data-ab-qa-body]');
  const scrollTop = body ? body.scrollTop : 0;
  panel.innerHTML = html;
  const nextBody = panel.querySelector('[data-ab-qa-body]');
  if (nextBody) nextBody.scrollTop = scrollTop;
}

/* ---------- open / close ---------- */

async function open(handle, trigger) {
  if (!handle) return;
  ensureRoot();
  lastTrigger = trigger || null;
  if (trigger) trigger.setAttribute('aria-busy', 'true');
  let data;
  try {
    data = await getData(handle);
  } catch (error) {
    console.warn('[AB quick add] could not load product data', error);
    if (trigger) trigger.removeAttribute('aria-busy');
    window.location.href = `${ROOT}products/${handle}`;
    return;
  }
  if (trigger) trigger.removeAttribute('aria-busy');
  if (!data || !data.items || !data.items.length) return;

  const itemIndex = Math.max(
    0,
    data.items.findIndex((i) => i.current)
  );
  const item = data.items[itemIndex];

  // One-size products: add straight away, same as the live mobile bar.
  if (item.variants.length === 1 && item.variants[0].available) {
    await addToCart(item.variants[0].id, cartProperties(data, item));
    return;
  }

  state = {
    adding: false,
    data,
    itemIndex,
    mode: desktopQuery.matches ? 'drawer' : 'sheet',
    variantId: null,
  };
  root.dataset.mode = state.mode;
  // Re-append so the panel is the last element on the page: third-party widgets injected
  // after load can share the max z-index, and the later element wins.
  if (document.body.lastElementChild !== root) document.body.appendChild(root);
  hideFloatingWidgets();
  render();
  clearTimeout(closeTimer);
  root.hidden = false;
  document.documentElement.classList.add('ab-qa-lock');
  requestAnimationFrame(() => {
    root.classList.add('is-open');
    const close = panel.querySelector('.ab-qa__close');
    if (close) close.focus({ preventScroll: true });
  });
}

/* Third-party floating widgets that sit at the bottom of the viewport (discount teaser, AI
   shopping assistant pill, chat). Anything fixed, outside the panel, overlapping the bottom
   200px and above the page content gets hidden while quick add is open. */
let hiddenWidgets = [];
function hideFloatingWidgets() {
  hiddenWidgets = [];
  const limit = window.innerHeight - 200;
  for (const el of document.body.children) {
    if (el === root || el.tagName === 'SCRIPT' || el.tagName === 'MAIN' || el.tagName === 'HEADER') continue;
    const candidates = [el, ...el.querySelectorAll(':scope > *, :scope > * > *')];
    for (const node of candidates) {
      const style = getComputedStyle(node);
      if (style.position !== 'fixed' || style.visibility === 'hidden' || style.display === 'none') continue;
      const rect = node.getBoundingClientRect();
      if (!rect.width || !rect.height || rect.bottom < limit) continue;
      if (rect.height > window.innerHeight * 0.6) continue; // full-screen layers (drawers, overlays)
      hiddenWidgets.push([node, node.style.visibility]);
      node.style.visibility = 'hidden';
      break;
    }
  }
}
function restoreFloatingWidgets() {
  hiddenWidgets.forEach(([node, value]) => {
    node.style.visibility = value;
  });
  hiddenWidgets = [];
}

function close() {
  if (!root || root.hidden) return;
  restoreFloatingWidgets();
  root.classList.remove('is-open');
  document.documentElement.classList.remove('ab-qa-lock');
  closeTimer = setTimeout(() => {
    root.hidden = true;
    state = null;
  }, 320);
  if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus({ preventScroll: true });
}

function cartProperties(data, item) {
  return {
    _color_name: item.colorFull,
    _compare_at_price: item.compareCents ? item.compareCents / 100 : null,
    _is_final_sale: item.finalSale,
    _master_name: data.masterName,
  };
}

async function addSelected(variantId) {
  if (!state || state.adding) return;
  const { data } = state;
  const item = currentItem();
  state.adding = true;
  if (state.mode === 'drawer') render();
  try {
    close();
    await addToCart(variantId, cartProperties(data, item));
  } catch (error) {
    console.warn('[AB quick add] add to cart failed', error);
  }
}

/* ---------- events ---------- */

function onPanelClick(event) {
  if (!state) return;
  if (event.target.closest('[data-ab-qa-close]')) {
    close();
    return;
  }
  const color = event.target.closest('[data-ab-qa-color]');
  if (color) {
    const index = Number(color.dataset.abQaColor);
    if (index === state.itemIndex) return;
    const previous = currentItem().variants.find((v) => v.id === state.variantId);
    state.itemIndex = index;
    const match = previous && currentItem().variants.find((v) => v.title === previous.title && v.available);
    state.variantId = match ? match.id : null;
    render();
    return;
  }
  const size = event.target.closest('[data-ab-qa-size]');
  if (size && !size.disabled) {
    const id = Number(size.dataset.abQaSize);
    if (state.mode === 'sheet') {
      addSelected(id);
    } else {
      state.variantId = id;
      render();
    }
    return;
  }
  if (event.target.closest('[data-ab-qa-add]') && state.variantId) {
    addSelected(state.variantId);
  }
}

function onPanelKeydown(event) {
  if (event.key === 'Escape') {
    close();
    return;
  }
  if (event.key !== 'Tab') return;
  const focusable = [...panel.querySelectorAll('a[href], button:not([disabled])')];
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

document.addEventListener(
  'click',
  (event) => {
    const trigger = event.target.closest('[data-ab-qa-open]');
    if (!trigger) return;
    event.preventDefault();
    event.stopPropagation();
    const card = trigger.closest('[data-product-card]');
    open(handleFromCard(card), trigger);
  },
  true
);

desktopQuery.addEventListener('change', close);

/* ---------- ④ flip: "+N" overflow pill ---------- */

function updateOverflow(card) {
  const row = card.querySelector('[data-ab-qa-row]');
  const more = card.querySelector('[data-ab-qa-more]');
  if (!row || !more) return;
  const pills = [...row.children];
  const overflowing = row.scrollWidth > row.clientWidth + 1;
  const viewEnd = row.scrollLeft + row.clientWidth;
  const hiddenCount = pills.filter((p) => p.offsetLeft + p.offsetWidth > viewEnd + 1).length;
  more.hidden = !overflowing || hiddenCount === 0;
  more.textContent = `+${hiddenCount}`;
  more.setAttribute('aria-label', `Show ${hiddenCount} more sizes`);
  row.classList.toggle('is-overflowing', overflowing && hiddenCount > 0);
}

let lastFlipCard = null;
document.addEventListener('mouseover', (event) => {
  if (document.documentElement.dataset.abQa !== 'flip') return;
  const card = event.target.closest && event.target.closest('.ab-qa-card');
  if (!card || card === lastFlipCard) return;
  lastFlipCard = card;
  updateOverflow(card);
});
document.addEventListener('focusin', (event) => {
  if (document.documentElement.dataset.abQa !== 'flip') return;
  const card = event.target.closest && event.target.closest('.ab-qa-card');
  if (card) updateOverflow(card);
});
document.addEventListener('click', (event) => {
  const more = event.target.closest && event.target.closest('[data-ab-qa-more]');
  if (!more) return;
  event.preventDefault();
  event.stopPropagation();
  const card = more.closest('[data-product-card]');
  const row = card && card.querySelector('[data-ab-qa-row]');
  if (row) row.scrollBy({ behavior: 'smooth', left: row.clientWidth - 48 });
});
document.addEventListener(
  'scroll',
  (event) => {
    const row = event.target;
    if (!(row instanceof Element) || !row.matches('[data-ab-qa-row]')) return;
    const card = row.closest('[data-product-card]');
    if (card) updateOverflow(card);
  },
  true
);
